import apiService from "./api";

export interface BillingStatus {
	status: string;
	trialEndsAt: string | null;
	currentPeriodEnd: string | null;
	subscribedAt: string | null;
	cancelledAt: string | null;
	plan: {
		name: string;
		slug: string;
		priceMonthly: string;
	} | null;
	addons?: Record<string, { planId: string; since?: string; tier?: string }>;
	pendingProration?: number;
	hasScheduledChange?: boolean;
	/**
	 * Checkout aberto e NÃO concluído — intenção de contratação, não contrato.
	 * Vem separado de `plan` de propósito: enquanto os dois moravam no mesmo
	 * campo, a tela anunciava plano contratado para quem desistiu no Mercado
	 * Pago. `checkoutUrl` é nulo nas intenções recuperadas pelo backfill (a URL
	 * nunca foi persistida) — sem link, o caminho é contratar de novo.
	 */
	pendingCheckout?: {
		planName: string | null;
		planSlug: string | null;
		priceMonthly: number | null;
		checkoutUrl: string | null;
		createdAt: string | null;
	} | null;
	/**
	 * Voucher que ainda vale. `isComped` = cortesia (ACTIVE sem cobrança no
	 * MP): o caminho para pagar é Assinar. Fora disso é desconto parcial, que
	 * o cliente pode encerrar com "Pagar valor integral".
	 */
	voucher?: {
		code: string;
		type: "FREE" | "PERCENTAGE" | "FIXED_AMOUNT";
		discountPercent: number | null;
		discountAmount: number | null;
		durationCycles: number | null;
		endsAt: string | null;
		isComped: boolean;
	} | null;
}

export interface BillingPayment {
	id: string;
	amount: string;
	status: string;
	paidAt: string | null;
	periodStart: string;
	periodEnd: string;
	createdAt: string;
}

export interface PublicPlan {
	id: string;
	name: string;
	slug: string;
	priceMonthly: string;
	description: string | null;
	kind: "BASE" | "ADDON";
	addonKey: string | null;
}

export interface EntitlementLimit {
	key: string;
	label: string;
	type: "hard" | "soft";
	limit: number | null; // null = ilimitado
	usage: number;
}

export interface EntitlementsSummary {
	modules: Record<string, boolean>;
	limits: EntitlementLimit[];
}

export const billingService = {
	async getPlans(): Promise<PublicPlan[]> {
		return apiService.get("/billing/plans");
	},

	async getStatus(): Promise<BillingStatus> {
		return apiService.get("/billing/status");
	},

	async getPayments(): Promise<BillingPayment[]> {
		return apiService.get("/billing/payments");
	},

	async getEntitlements(): Promise<EntitlementsSummary> {
		return apiService.get("/billing/entitlements");
	},

	async validateVoucher(
		code: string,
		planSlug?: string,
	): Promise<{
		valid: boolean;
		type: string;
		description?: string;
		originalPrice: string;
		finalPrice: string;
		discount: string;
		discountPercent: number | null;
		discountAmount: string | null;
	}> {
		return apiService.post("/billing/validate-voucher", { code, planSlug });
	},

	async subscribe(
		planSlug?: string,
		voucherCode?: string,
		addonSlugs?: string[],
	): Promise<{ checkoutUrl: string | null; activated: boolean }> {
		return apiService.post("/billing/subscribe", {
			planSlug,
			voucherCode: voucherCode || undefined,
			addonSlugs: addonSlugs && addonSlugs.length ? addonSlugs : undefined,
		});
	},

	async cancel(): Promise<{ message: string }> {
		return apiService.post("/billing/cancel");
	},

	/** "Pagar valor integral": encerra o desconto parcial vigente. */
	async endVoucher(): Promise<{ effect: "FULL_PRICE" | "PAST_DUE" }> {
		return apiService.post("/billing/voucher/end");
	},

	/** Troca o plano BASE (upgrade imediato + proporcional; downgrade agendado). */
	async changePlan(
		planSlug: string,
	): Promise<{
		activated: boolean;
		proration?: number;
		effective: "now" | "period_end";
		at?: string | null;
	}> {
		return apiService.post("/billing/change-plan", { planSlug });
	},

	/** Contrata / sobe faixa de um add-on (acesso imediato + proporcional). */
	async setAddon(
		addonSlug: string,
	): Promise<{
		activated: boolean;
		proration?: number;
		effective: "now" | "period_end";
		at?: string | null;
	}> {
		return apiService.post("/billing/addons", { addonSlug });
	},

	/** Remove um add-on (agendado para o fim do período). */
	async removeAddon(
		addonKey: string,
	): Promise<{ effective: "period_end"; at?: string | null }> {
		return apiService.delete(`/billing/addons/${addonKey}`);
	},
};
